import { Fragment, useEffect, useState } from 'react';

import { getTutorials } from '../../../../store/tutorials';
import { tutorialConstants } from '../../../../utils/constants';
import { useAppSelector } from '../../../../store/configureStore';

import { ETSummarySheetData, HelideckSummaryOverview, PDFCoverSheetProps, PDFImageProps, SortColumn, VTEquipmentAnomalyData } from '../../../../utils/models';
import { getHelideckLineGraphData, getDefaultHelideckSummary, getETSummaryDummyData } from '../../../../utils/data';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import HelideckCoverForm from '../../../forms/helideckCoverForm';
import HelideckDrawingSheetForm from '../../../forms/HelideckDrawingSheetForm';
import HelideckInspection from '../../../blocks/helideck-inspection';
import HelideckInspectionDryForm from '../../../forms/HelideckInspectionDryForm';
import HelideckInspectionWetForm from '../../../forms/HelideckInspectionWetForm';
import HelideckPhotographicRecord from '../../../forms/helideckPhotographicRecord';
import HelideckSummaryForm from '../../../forms/helideckSummaryForm';
import ETSummaryForm from '../../../forms/et/etSummaryForm';
import HelideckReport from './';
import Modal from '../../../themed/modal';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateHelideckReport = () => {
	const [coverSheetData, setCoverSheetData] = useState<PDFCoverSheetProps>();
	const [drawingSheetData, setDrawingSheetData] = useState<PDFImageProps[]>([]);
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [photographicData, setPhotographicData] = useState<any>();
	const [graphDryData, setGraphDryData] = useState<any[]>([]);
	const [graphWetData, setGraphWetData] = useState<any[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [summarySheetData, setSummarySheetData] = useState<HelideckSummaryOverview>(getDefaultHelideckSummary());
	const [inspectionDryData, setInspectionDryData] = useState<PDFImageProps[]>([]);
	const [inspectionWetData, setInspectionWetData] = useState<PDFImageProps[]>([]);
	const [visualReportData, setVisualReportData] = useState<ETSummarySheetData>(getETSummaryDummyData());

	const getReportDetails = () => {
		return {
			coverSheetData,
			anomalyData,
			summarySheetData,
			drawingSheetPhotos: drawingSheetData,
			drySketchPhotos: inspectionDryData,
			wetSketchPhotos: inspectionWetData,
			visualReportData,
			dryTableData: graphDryData,
			wetTableData: graphWetData,
			tabularSortColumn: sortColumn,
			photographicRecords: photographicData,
			projectDetails,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setCoverSheetData(cachedReport.coverSheetData);
			setSummarySheetData(cachedReport.summarySheetData);
			setDrawingSheetData(cachedReport.drawingSheetPhotos);
			setInspectionDryData(cachedReport.drySketchPhotos);
			setInspectionWetData(cachedReport.wetSketchPhotos);
			setPhotographicData(cachedReport.photographicRecords);
			setVisualReportData(cachedReport.visualReportData);
			setGraphDryData(cachedReport.dryTableData);
			setGraphWetData(cachedReport.wetTableData);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<HelideckReport {...reportDetails} />
			</Modal>

			<Fragment>
				<Zippy headerTitle="Cover Sheet" isValid={!!coverSheetData}>
					<HelideckCoverForm onProceed={setCoverSheetData} />
				</Zippy>

				<Zippy
					headerTitle="Summary Sheet"
					isValid={!!summarySheetData.dryMethodResult}
				>
					<HelideckSummaryForm onProceed={setSummarySheetData} />
				</Zippy>

				<Zippy
					headerTitle="Drawing Sheet"
					isValid={drawingSheetData.length > 0}
				>
					<HelideckDrawingSheetForm onProceed={setDrawingSheetData} />
				</Zippy>

				<Zippy
					headerTitle="Inspection Sketch - Dry Condition"
					isValid={inspectionDryData.length > 0}
				>
					<HelideckInspectionDryForm onProceed={setInspectionDryData} />
				</Zippy>

				<Zippy
					headerTitle="Inspection Sketch - Wet Condition"
					isValid={inspectionWetData.length > 0}
				>
					<HelideckInspectionWetForm onProceed={setInspectionWetData} />
				</Zippy>

				<Zippy
					headerTitle="Visual Inspection Record"
					isValid={visualReportData.inspectedItems.length > 0}
				>
					<ETSummaryForm onProceed={setVisualReportData} />
				</Zippy>

				<Zippy
					headerTitle="Helideck Data and Graph - Dry Condition"
					isValid={graphDryData.length > 0}
				>
					<HelideckInspection
						data={graphDryData}
						lineGraphData={getHelideckLineGraphData(graphDryData)}
						onDataChange={setGraphDryData}
					/>
				</Zippy>

				<Zippy
					headerTitle="Helideck Data and Graph - Wet Condition"
					isValid={graphWetData.length > 0}
				>
					<HelideckInspection
						lineGraphData={getHelideckLineGraphData(graphWetData)}
						data={graphWetData}
						onDataChange={setGraphWetData}
					/>
				</Zippy>

				<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
					<ETAnomalySummary
						anomalyData={anomalyData}
						onDataChange={setAnomalyData}
					/>
				</Zippy>

				<Zippy headerTitle="Photographic Record" isValid={!!photographicData}>
					<HelideckPhotographicRecord onProceed={setPhotographicData} />
				</Zippy>

				<div className="d-flex justify-content-end">
					<Elevation
						isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
					>
						<Button
							label="Save and Preview Report"
							disabled={error}
							onClick={handleSaveAndPreview}
						/>
					</Elevation>
				</div>
			</Fragment>
		</Fragment>
	);
};

const sortColumn: SortColumn = {
	path: 'gridNo',
	order: 'desc',
};

export default CreateHelideckReport;
